import { FC } from 'react';
import { Campaign } from 'types';
import { TGroup } from '../types';
import AdvancedCurrentPeopleApiFilter from './AdvancedCurrentPeopleApiFilter';
import AdvancedCurrentPeopleFilter from './AdvancedCurrentPeopleFilter';
import AdvancedDatasetApiFilter from './AdvancedDatasetApiFilter';
import {
  AdvancedCurrentPeopleFilterContextProvider,
  AdvancedDatasetFilterContextProvider,
} from './context';
import { ApiGroup } from './types';

type Props = {
  value: TGroup | undefined;
  onChange?: (value: TGroup | undefined) => void;
  onValidChange?: (message: string | null) => void;
  disabled?: boolean;
  searchBarPlaceholder?: string;
};

export const AdvancedCurrentPeopleFilterComponent: FC<Props> = (props) => {
  return (
    <AdvancedCurrentPeopleFilterContextProvider roleScope="SYSTEM_ADMIN">
      <AdvancedCurrentPeopleFilter {...props} />
    </AdvancedCurrentPeopleFilterContextProvider>
  );
};

type ApiProps = {
  value: ApiGroup | undefined;
  onChange?: (value: ApiGroup | undefined) => void;
  onValidChange?: (message: string | null) => void;
  disabled?: boolean;
  searchBarPlaceholder?: string;
};

type DatasetApiProps = ApiProps & { campaign: Campaign };

export const AdvancedCurrentPeopleFilterApiComponent: FC<ApiProps> = (
  props
) => {
  return (
    <AdvancedCurrentPeopleFilterContextProvider roleScope="SYSTEM_ADMIN">
      <AdvancedCurrentPeopleApiFilter {...props} />
    </AdvancedCurrentPeopleFilterContextProvider>
  );
};

export const AdvancedDatasetFilterHrbpApiComponent: FC<DatasetApiProps> = (
  props
) => {
  const { campaign, ...other } = props;
  return (
    <AdvancedDatasetFilterContextProvider
      roleScope="HRBP"
      campaignId={campaign.id}
    >
      <AdvancedDatasetApiFilter {...other} />
    </AdvancedDatasetFilterContextProvider>
  );
};

export const AdvancedDatasetFilterWithIndividualsHrbpApiComponent: FC<
  DatasetApiProps
> = (props) => {
  const { campaign, ...other } = props;
  return (
    <AdvancedDatasetFilterContextProvider
      roleScope="HRBP"
      campaignId={campaign.id}
      includeIndividualsFilters={true}
    >
      <AdvancedDatasetApiFilter {...other} />
    </AdvancedDatasetFilterContextProvider>
  );
};

export const AdvancedDatasetFilterWithIndividualsCalibrationViewApiComponent: FC<
  DatasetApiProps & { calibrationViewId: number }
> = (props) => {
  const { campaign, calibrationViewId, ...other } = props;
  const additionalData = { calibration_view_id: calibrationViewId.toString() };
  return (
    <AdvancedDatasetFilterContextProvider
      roleScope="CALIBRATION_VIEW_ADMINISTRATOR"
      campaignId={campaign.id}
      includeIndividualsFilters={true}
      additionalData={additionalData}
    >
      <AdvancedDatasetApiFilter {...other} />
    </AdvancedDatasetFilterContextProvider>
  );
};

// Reviewees of a calibration view created by a CALIBRATION_VIEW_EDITOR who is
// not an HRBP: the people in the target audience of their role
export const AdvancedDatasetFilterWithIndividualsCalibrationViewEditorApiComponent: FC<
  DatasetApiProps
> = (props) => {
  const { campaign, ...other } = props;
  return (
    <AdvancedDatasetFilterContextProvider
      roleScope="CALIBRATION_VIEW_EDITOR"
      campaignId={campaign.id}
      includeIndividualsFilters={true}
    >
      <AdvancedDatasetApiFilter {...other} />
    </AdvancedDatasetFilterContextProvider>
  );
};

export const AdvancedCurrentPeopleFilterRoleUserApiComponent: FC<ApiProps> = (
  props
) => {
  return (
    <AdvancedCurrentPeopleFilterContextProvider roleScope="USER">
      <AdvancedCurrentPeopleApiFilter {...props} />
    </AdvancedCurrentPeopleFilterContextProvider>
  );
};

export const AdvancedCurrentPeopleRoleUserBelowCOCAdminableFilterApiComponent: FC<
  ApiProps
> = (props) => {
  return (
    <AdvancedCurrentPeopleFilterContextProvider
      roleScope="USER"
      population="BELOW_CHAIN_OF_COMMAND_OR_ADMINABLE"
    >
      <AdvancedCurrentPeopleApiFilter {...props} />
    </AdvancedCurrentPeopleFilterContextProvider>
  );
};

export const AdvancedCurrentPeopleRoleOneOnOnesAdminApiComponent: FC<
  ApiProps
> = (props) => {
  return (
    <AdvancedCurrentPeopleFilterContextProvider roleScope="ONE_ON_ONES_ADMIN">
      <AdvancedCurrentPeopleApiFilter {...props} />
    </AdvancedCurrentPeopleFilterContextProvider>
  );
};

export const AdvancedCustomSurveyFilterApiComponent: FC<
  ApiProps & { surveyId: number }
> = ({ surveyId, ...props }) => {
  return (
    <AdvancedCurrentPeopleFilterContextProvider
      roleScope="CUSTOM_SURVEY"
      extras={{ surveyId }}
    >
      <AdvancedCurrentPeopleApiFilter {...props} />
    </AdvancedCurrentPeopleFilterContextProvider>
  );
};

export const AdvancedCustomSurveyAudienceFilterApiComponent: FC<
  ApiProps & { surveyId: number }
> = ({ surveyId, ...props }) => {
  return (
    <AdvancedCurrentPeopleFilterContextProvider
      roleScope="CUSTOM_SURVEY_AUDIENCE"
      extras={{ surveyId }}
    >
      <AdvancedCurrentPeopleApiFilter {...props} />
    </AdvancedCurrentPeopleFilterContextProvider>
  );
};
